import { useState, useEffect } from 'react';
import { StaticData } from '@/components/common/static-section/static';

interface UseDynamicContentOptions {
  slug: string[];
  searchParams?: URLSearchParams;
  fallbackContent?: StaticData;
  enabled?: boolean; // If false, skip fetching (for static pages)
}

interface UseDynamicContentReturn {
  data: StaticData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// Dynamic content is always fetched fresh; no in-memory cache is kept so that
// backend changes are reflected immediately.

export function useDynamicContent({
  slug,
  searchParams = new URLSearchParams(),
  fallbackContent,
  enabled = true
}: UseDynamicContentOptions): UseDynamicContentReturn {
  const [data, setData] = useState<StaticData | null>(null);
  const [isLoading, setIsLoading] = useState(enabled); // Only loading if enabled
  const [error, setError] = useState<Error | null>(null);

  const slugPath = slug.join('/');
  const queryString = searchParams.toString();
  const hasLang = searchParams.has('lang');

  useEffect(() => {
    // Skip fetching if disabled (static page)
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    // Don't fetch if searchParams doesn't have lang parameter yet
    if (!hasLang) {
      setIsLoading(true);
      return;
    }

    const fetchContent = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch from API - use only last segment of slug
        const lastSegment = slug[slug.length - 1];
        const url = `/api/dynamic-content/${lastSegment}${queryString ? `?${queryString}` : ''}`;
        console.log('useDynamicContent - Fetching:', { slug, lastSegment, url });
        const response = await fetch(url, { cache: 'no-store' });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          setData(result.data);
        } else {
          throw new Error(result.error || 'Failed to fetch content');
        }
      } catch (err) {
        const errorObj = err as Error;
        setError(errorObj);
        
        // Use fallback content if available
        if (fallbackContent) {
          setData(fallbackContent);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, [slug, slugPath, queryString, fallbackContent, hasLang, enabled]);

  const refetch = () => {
    setIsLoading(true);
  };

  return {
    data,
    isLoading,
    error,
    refetch
  };
}

// Hook for managing content creation/editing (for admin interfaces)
export function useContentManagement() {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);

  const saveContent = async (path: string, content: StaticData) => {
    try {
      setIsSaving(true);
      setSaveError(null);

      const response = await fetch(`/api/dynamic-content/${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          path,
          customContent: content
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to save content');
      }

      return result;
    } catch (err) {
      const errorObj = err as Error;
      setSaveError(errorObj);
      throw errorObj;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    saveContent,
    isSaving,
    saveError
  };
}

// Kept for API compatibility; there is no cache to clear anymore.
export function clearContentCache(_pathPrefix?: string) {
  void _pathPrefix;
}

// Hook for preloading content (useful for link hover effects)
export function useContentPreloader() {
  const preloadContent = async (slug: string[], searchParams?: URLSearchParams) => {
    const fullPath = slug.join('/');
    const queryString = searchParams?.toString() || '';

    try {
      const url = `/api/dynamic-content/${fullPath}${queryString ? `?${queryString}` : ''}`;
      const response = await fetch(url, { cache: 'no-store' });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          return result.data;
        }
      }
    } catch (error) {
      console.warn('Failed to preload content:', error);
    }

    return null;
  };

  return { preloadContent };
}